import React, { FC, Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button } from '@mui/material';
import DHDialog from 'components/shared/dh-dialog/dh-dialog.component';
import { ConstructionProperty } from 'models/property';
import AiAlert from 'components/shared/ai-alert/ai-alert.component';
import { SimpleSubCategory } from 'models/order-input/simple-order';
import { RestorationInput } from 'models/order-input/restoration-input';
import {
    RESTORATION_TYPE_BY_SIMPLE_CATEGORY,
    jawTeethInputFromRange,
    propertiesToInputs,
    teethInputFromNumbers,
} from 'components/order-flow/shared/restoration-input-helper';
import { TeethRange } from 'models/material';
import ConstructionForm from 'components/order-flow/shared/constructions/add-edit-construction/construction-form/construction-form.component';

type AiCreateConstructionsDialogProps = {
    category: SimpleSubCategory;
    open: boolean;
    onClose: () => void;
    onAddRestoration?: (restoration: RestorationInput) => void;
    settings: ConstructionProperty;
    teeth?: number[];
    teethRange?: TeethRange;
};
const AiCreateConstructionDialog: FC<AiCreateConstructionsDialogProps> = ({
    category,
    open,
    onClose,
    onAddRestoration,
    settings,
    teeth,
    teethRange,
}) => {
    const { t: translate } = useTranslation(['orders', 'common']);
    const [selectedCategory, setSelectedCategory] = useState<SimpleSubCategory>();
    const [currentRestoration, setCurrentRestoration] = useState<RestorationInput>();
    // What the assistant found, as the restoration the form starts from.
    const suggestion = useMemo<RestorationInput>(
        () => ({
            type: RESTORATION_TYPE_BY_SIMPLE_CATEGORY[category],
            teeth: teethRange ? jawTeethInputFromRange(teethRange) : teethInputFromNumbers(teeth),
            properties: propertiesToInputs(settings),
            actions: [],
            materials: [],
        }),
        [category, settings, teeth, teethRange],
    );

    useEffect(() => {
        setSelectedCategory(category);
    }, [category]);

    const handleClose = () => {
        onClose();
        // Prevent glitch when closing dialog
        setTimeout(() => {
            setCurrentRestoration(undefined);
        }, 500);
    };

    const handleCreateButtonClicked = () => {
        if (currentRestoration && onAddRestoration) {
            onAddRestoration(currentRestoration);
        }
        handleClose();
    };

    const aiMessage = () => {
        return `${translate(`${category}`)} - ${translate('orders:AI_FOUND_CONSTRUCTION')}`;
    };

    return (
        <DHDialog
            open={open}
            onClose={handleClose}
            disableBackdropClick
            maxWidth="xl"
            fullWidth
            title={selectedCategory ? translate(`orders:${selectedCategory}`) : translate('orders:ADD_CONSTRUCTION')}
            actions={
                <Button
                    variant="contained"
                    disabled={!currentRestoration}
                    onClick={handleCreateButtonClicked}
                    data-test="ai-create-construction-dialog.add-btn"
                >
                    {translate('orders:ADD_CONSTRUCTION')}
                </Button>
            }
            data-test="ai-create-construction-dialog.dialog"
        >
            {selectedCategory ? (
                <Fragment>
                    <Box mb={1}>
                        <AiAlert title={translate('common:AI_ASSISTANT')} message={aiMessage()} />
                    </Box>
                    <ConstructionForm
                        category={selectedCategory}
                        restoration={suggestion}
                        onRestorationChange={setCurrentRestoration}
                    />
                </Fragment>
            ) : undefined}
        </DHDialog>
    );
};

export default AiCreateConstructionDialog;
